import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';

/**
 * Stub page used by sidebar routes that don't have their own component yet
 * (Home, Alerts). Reads the route's `data.label` — a `breadcrumbs.*` dictionary KEY
 * (rule 11), so the template pipes it through `translate`; a route without a label
 * falls back to the generic "Coming soon" title key.
 * Replace this binding in app.routes.ts as real pages are built.
 */
@Component({
  selector: 'app-placeholder-page',
  standalone: true,
  imports: [CommonModule, AsyncPipe, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="placeholder">
      <h2 class="placeholder__title">{{ (labelKey$ | async) ?? 'placeholder.comingSoonTitle' | translate }}</h2>
      <p  class="placeholder__caption">{{ 'placeholder.comingSoonCaption' | translate }}</p>
    </section>
  `,
  styles: [`
    :host { display: block; }
    .placeholder {
      padding: 64px 24px;
      text-align: center;
      font-family: 'Poppins', system-ui, sans-serif;
    }
    .placeholder__title   { margin: 0 0 8px; font-weight: 600; font-size: 22px; color: #3D3D3D; }
    .placeholder__caption { margin: 0; font-weight: 400; font-size: 14px; color: #808080; }
  `],
})
export class PlaceholderPageComponent {
  private route = inject(ActivatedRoute);
  /** The route's `data.label` dictionary key (resolved in the template), or the fallback key. */
  labelKey$: Observable<string> = this.route.data.pipe(
    map(d => (d['label'] as string | undefined) ?? 'placeholder.comingSoonTitle'),
  );
}
